/**
 * Install prompt banner: captures the browser's `beforeinstallprompt`
 * event and offers a one-tap "add to home screen" action, since Chrome and
 * other Chromium browsers suppress their own mini-infobar once the event
 * is captured in script.
 */
import { useEffect, useState } from 'react';

import { Button, Card } from '../components/ui';
import { t } from '../i18n/fr';

/** The non-standard event Chromium fires when a PWA becomes installable. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Render the install banner once the browser signals the app is
 * installable, or nothing otherwise (unsupported browser, already
 * installed, or dismissed for this session).
 *
 * @returns The banner element, or `null`.
 */
export function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | undefined>(
    undefined,
  );
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  if (!deferredEvent || dismissed) {
    return null;
  }

  async function install() {
    if (!deferredEvent) {
      return;
    }
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    setDeferredEvent(undefined);
  }

  return (
    <div className="pwa-banner" role="status">
      <Card>
        <p className="ui-label">{t('pwa.install.title')}</p>
        <p>{t('pwa.install.body')}</p>
        <div className="pwa-banner__actions">
          <Button type="button" onClick={() => void install()}>
            {t('pwa.install.action')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setDismissed(true)}>
            {t('pwa.install.dismiss')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
